import { useDentalSectionReady } from "../../hooks/useDentalSectionReveal";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  animate,
  useTransform,
  type MotionValue,
} from "framer-motion";
import {
  CalendarCheck2,
  ChartNoAxesCombined,
  Clock3,
  PhoneIncoming,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  chartProductionMaximum,
  cumulativePracticeAppointments,
  cumulativePracticeProduction,
  estimatedCumulativeHoursSaved,
  estimatedMonthlyHoursSaved,
  estimatedPeriodHoursSaved,
  estimatedThirdMonthHoursSaved,
  examplePracticeDentists,
  firstMonthAppointmentsBooked,
  impactMonthDays,
  impactPeriodDays,
  periodAppointmentsBooked,
  periodPracticeProduction,
  weeklyFrontDeskHoursSaved,
} from "./dentalImpactFigures";

type MetricFormat = "percent" | "integer" | "production" | "hours";

type ImpactMetric = {
  icon: LucideIcon;
  value: string;
  label: string;
  color: string;
  surface: string;
  format: MetricFormat;
};

const appointmentNumberFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const productionNumberFormat = new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1,
});

const impactMetrics: ImpactMetric[] = [
  {
    icon: PhoneIncoming,
    value: "100%",
    label: "call answer rate",
    color: "#5b24c9",
    surface: "#f5f0ff",
    format: "percent",
  },
  {
    icon: CalendarCheck2,
    value: `${appointmentNumberFormat.format(periodAppointmentsBooked)}+`,
    label: "appointments",
    color: "#15945f",
    surface: "#edf9f2",
    format: "integer",
  },
  {
    icon: ChartNoAxesCombined,
    value: productionNumberFormat.format(periodPracticeProduction),
    label: "production",
    color: "#1768e7",
    surface: "#eff5ff",
    format: "production",
  },
  {
    icon: Clock3,
    value: `${estimatedPeriodHoursSaved} hrs`,
    label: "time saved",
    color: "#e85b0a",
    surface: "#fff5ed",
    format: "hours",
  },
];

// One horizontal reveal drives only the curve and its fill. The annotations
// have their own arrival-triggered transitions and are never inside this clip.
const revealStart = 63;
const revealWidth = 803;
const monthPositions = [260, 490, 730];
const monthArrivals = monthPositions.map((x) => (x - revealStart) / revealWidth);
const productionY = (production: number) => 290 - (production / chartProductionMaximum) * 224;
const monthPoints = monthPositions.map((x, index) => ({ x, y: productionY(cumulativePracticeProduction[index]) }));
const chartPoints = [{ x: 65, y: productionY(0) }, ...monthPoints];
// Carry the slope through each month so the chart accelerates continuously
// instead of flattening and repeating the same S shape at every milestone.
const curveTangents = chartPoints.map((point, index, points) => {
  if (index === 0) return 0;
  const previous = points[index - 1];
  const next = points[index + 1] ?? point;
  return (next.y - previous.y) / (next.x - previous.x);
});
const productionCurve = chartPoints.reduce((path, point, index, points) => {
  if (index === 0) return `M${point.x} ${point.y}`;
  const previous = points[index - 1];
  const handleWidth = (point.x - previous.x) / 3;
  return `${path} C${previous.x + handleWidth} ${previous.y + curveTangents[index - 1] * handleWidth} ${point.x - handleWidth} ${point.y - curveTangents[index] * handleWidth} ${point.x} ${point.y}`;
}, "");

function AnimatedCount({
  value,
  finalValue,
  format,
}: {
  value: MotionValue<number>;
  finalValue: string;
  format: MetricFormat;
}) {
  const display = useTransform(value, (current) => {
    if (format === "production") return productionNumberFormat.format(current);
    if (format === "hours") return `${Math.round(current)} hrs`;
    if (format === "integer") {
      const reachedTotal = Math.round(current) >= periodAppointmentsBooked;
      return `${appointmentNumberFormat.format(current)}${reachedTotal ? "+" : ""}`;
    }
    return `${Math.round(current)}%`;
  });

  return (
    <>
      <span className="sr-only">{finalValue}</span>
      <motion.span aria-hidden="true">{display}</motion.span>
    </>
  );
}

function ImpactMilestone({
  progress,
  arrival,
  x,
  y,
  guideTop,
  reduceMotion,
  children,
}: {
  progress: MotionValue<number>;
  arrival: number;
  x: number;
  y: number;
  guideTop: number;
  reduceMotion: boolean;
  children: ReactNode;
}) {
  const [reached, setReached] = useState(() => progress.get() >= arrival);
  useMotionValueEvent(progress, "change", (current) => setReached(current >= arrival));
  useEffect(() => setReached(progress.get() >= arrival), [progress, arrival]);
  const visible = reduceMotion || reached;

  return (
    <g data-impact-milestone={x}>
      <motion.line
        data-impact-guide=""
        x1={x} y1={guideTop} x2={x} y2="290"
        stroke="#b7cdf9" strokeWidth="1.2" strokeDasharray="4 6"
        initial={false}
        animate={{ opacity: visible ? 1 : 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.16 }}
      />
      <motion.g
        data-impact-popup=""
        initial={false}
        animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 6 }}
        transition={{
          duration: reduceMotion ? 0 : 0.22,
          delay: visible && !reduceMotion ? 0.18 : 0,
          ease: "easeOut",
        }}
      >
        {children}
      </motion.g>
      <circle
        cx={x} cy={y} r="9" fill="#fff" stroke="#1768e7" strokeWidth="4"
        filter="url(#dental-impact-node-shadow)" opacity={visible ? 1 : 0}
      />
    </g>
  );
}

export function DentalImpactGrowth() {
  const cardRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = Boolean(useReducedMotion());
  const ready = useDentalSectionReady(cardRef);
  const drawProgress = useMotionValue(0);
  useEffect(() => {
    if (shouldReduceMotion) { drawProgress.set(1); return; }
    drawProgress.set(0);
    if (!ready) return;
    const animation = animate(drawProgress, 1, { duration: 2.4, ease: "easeInOut" });
    return () => animation.stop();
  }, [ready, shouldReduceMotion, drawProgress]);
  const curveRevealWidth = useTransform(drawProgress, [0, 1], [0, revealWidth]);
  const statisticsProgress = useTransform(drawProgress, [0, monthArrivals[2]], [0, 1]);
  const answerRate = useTransform(statisticsProgress, [0, 1], [0, 100]);
  const appointmentsCount = useTransform(
    drawProgress, [0, ...monthArrivals], [0, ...cumulativePracticeAppointments],
  );
  const productionCount = useTransform(
    drawProgress, [0, ...monthArrivals], [0, ...cumulativePracticeProduction],
  );
  const savedHoursCount = useTransform(
    drawProgress, [0, ...monthArrivals], [0, ...estimatedCumulativeHoursSaved],
  );
  const metricCounts = [answerRate, appointmentsCount, productionCount, savedHoursCount];

  return (
    <section
      id="dental-practice-impact"
      aria-labelledby="dental-impact-title"
      className="relative scroll-mt-24 overflow-hidden bg-[#f8f6fa] px-5 py-8 sm:px-10 sm:py-10 lg:px-12"
    >
      <div className="dental-content relative">
        <header className="text-center">
          <h2 id="dental-impact-title" className="mx-auto max-w-[920px] text-balance text-[30px] font-extrabold leading-[1.12] tracking-[-.048em] text-[#172033] sm:text-[38px]">
            Answer every call. Book more appointments.
          </h2>
        </header>

        <div className="mt-6 grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div ref={cardRef} className="min-w-0 overflow-hidden rounded-[22px] border border-[#e8e1eb] bg-white shadow-[0_16px_44px_rgba(45,24,58,.07)]">
            <div className="grid grid-cols-2 divide-x divide-y divide-[#e8e1eb] border-b border-[#e8e1eb] sm:grid-cols-4 sm:divide-y-0">
              {impactMetrics.map(({ icon: Icon, value, label, color, surface, format }, index) => (
                <div key={label} className="flex min-w-0 flex-col justify-center px-3 py-4 xl:px-4">
                  <div className="flex items-center gap-2">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[8px] border border-black/[.035] xl:h-7 xl:w-7"
                      style={{ color, backgroundColor: surface }}
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.9} aria-hidden="true" />
                    </span>
                    <strong className="block whitespace-nowrap text-[22px] font-extrabold tabular-nums leading-none tracking-[-.04em] xl:text-[24px]" style={{ color }}>
                      <AnimatedCount
                        value={metricCounts[index]}
                        finalValue={value}
                        format={format}
                      />
                    </strong>
                  </div>
                  <span className="mt-2 block whitespace-nowrap text-[11px] font-bold leading-[1.4] text-[#596176]">{label}</span>
                </div>
              ))}
            </div>

            <div className="px-4 pb-4 pt-5 sm:px-5 sm:pb-5">
              <div className="px-2">
                <h3 className="text-[18px] font-extrabold tracking-[-.035em] text-[#172033]">{impactPeriodDays}-Day Operational Impact Estimate</h3>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-[.08em] text-[#586ca4] sm:text-[11px]">Cumulative practice production (USD)</p>
              </div>

              <svg
                viewBox="0 0 900 350"
                role="img"
                aria-label="Illustrative whole-practice activity over three months"
                aria-describedby="dental-impact-chart-description"
                className="mt-1 block h-auto w-full overflow-visible"
              >
                <desc id="dental-impact-chart-description">
                  Example scenario for a {examplePracticeDentists}-dentist clinic. Cumulative production is {cumulativePracticeProduction[0]} dollars after month one, {cumulativePracticeProduction[1]} dollars after month two, and {periodPracticeProduction} dollars after month three. The scenario starts with {firstMonthAppointmentsBooked} appointments in month one and illustrates at least {periodAppointmentsBooked} appointments over {impactPeriodDays} days, displayed as {appointmentNumberFormat.format(periodAppointmentsBooked)}+. These are whole-practice figures, not additional revenue or bookings attributed to ClawEase. Production is the value of care provided, not profit or cash collected. Estimated front-desk time savings rise from {estimatedMonthlyHoursSaved[0]} hours in month one to {estimatedMonthlyHoursSaved[1]} hours in month two and {estimatedThirdMonthHoursSaved} hours in month three, totaling {estimatedPeriodHoursSaved} hours and reaching about {weeklyFrontDeskHoursSaved} hours per week using {impactMonthDays}-day months. All figures are illustrative assumptions, not measured customer results. The dashed continuation is decorative, not a forecast.
                </desc>

                <defs>
                  <clipPath id="dental-impact-curve-reveal" clipPathUnits="userSpaceOnUse">
                    <motion.rect x={revealStart} y="0" width={curveRevealWidth} height="350" />
                  </clipPath>
                  <linearGradient id="dental-impact-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7da9ff" stopOpacity="0.23" />
                    <stop offset="100%" stopColor="#dce8ff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="dental-impact-continuation" x1="730" y1="0" x2="865" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#1768e7" stopOpacity="0.72" />
                    <stop offset="100%" stopColor="#8bb2ff" stopOpacity="0.12" />
                  </linearGradient>
                  <filter id="dental-impact-node-shadow" x="-70%" y="-70%" width="240%" height="240%">
                    <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#366fe0" floodOpacity="0.22" />
                  </filter>
                  <filter id="dental-impact-card-shadow" x="-20%" y="-20%" width="140%" height="150%">
                    <feDropShadow dx="0" dy="7" stdDeviation="8" floodColor="#3d2954" floodOpacity="0.11" />
                  </filter>
                </defs>

                {[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
                  <g key={fraction}>
                    <line x1="65" y1={productionY(fraction * chartProductionMaximum)} x2="865" y2={productionY(fraction * chartProductionMaximum)} stroke="#e4e8f0" strokeWidth="1" strokeDasharray="4 6" />
                    <text x="53" y={productionY(fraction * chartProductionMaximum) + 4} textAnchor="end" fill="#526078" fontSize="11" fontWeight="600">
                      {productionNumberFormat.format(fraction * chartProductionMaximum)}
                    </text>
                  </g>
                ))}

                <g clipPath="url(#dental-impact-curve-reveal)">
                <path
                  d={`${productionCurve} L730 290 L65 290 Z`}
                  fill="url(#dental-impact-area)"
                />
                <path
                  d={productionCurve}
                  fill="none"
                  stroke="#1768e7"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d={`M730 ${monthPoints[2].y} C783 ${monthPoints[2].y + curveTangents[3] * 53} 811 ${monthPoints[2].y - 36} 865 ${monthPoints[2].y - 36}`}
                  fill="none"
                  stroke="url(#dental-impact-continuation)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="7 8"
                  vectorEffect="non-scaling-stroke"
                  opacity="0.9"
                />
                </g>

                <ImpactMilestone progress={drawProgress} arrival={monthArrivals[0]} x={260} y={monthPoints[0].y} guideTop={monthPoints[0].y - 20} reduceMotion={shouldReduceMotion}>
                  <g transform={`translate(0 ${monthPoints[0].y - 90})`}>
                    <rect x="156" y="0" width="208" height="64" rx="14" fill="#fff" stroke="#e5e0e9" filter="url(#dental-impact-card-shadow)" />
                    <rect x="179" y="15" width="32" height="32" rx="9" fill="#edf9f2" />
                    <text x="195" y="37" textAnchor="middle" fill="#15945f" fontSize="17" fontWeight="800">✓</text>
                    <text x="221" y="27" fill="#15945f" fontSize="22" fontWeight="800">{firstMonthAppointmentsBooked}</text>
                    <text x="221" y="47" fill="#26324b" fontSize="12.5" fontWeight="600">Booked in month 1</text>
                  </g>
                </ImpactMilestone>

                <ImpactMilestone progress={drawProgress} arrival={monthArrivals[1]} x={490} y={monthPoints[1].y} guideTop={monthPoints[1].y - 20} reduceMotion={shouldReduceMotion}>
                  <g transform={`translate(0 ${monthPoints[1].y - 90})`}>
                    <rect x="386" y="0" width="208" height="64" rx="14" fill="#fff" stroke="#e5e0e9" filter="url(#dental-impact-card-shadow)" />
                    <rect x="409" y="15" width="32" height="32" rx="9" fill="#eff5ff" />
                    <text x="425" y="38" textAnchor="middle" fill="#1768e7" fontSize="17" fontWeight="800">$</text>
                    <text x="451" y="27" fill="#1768e7" fontSize="22" fontWeight="800">{productionNumberFormat.format(cumulativePracticeProduction[1])}</text>
                    <text x="451" y="47" fill="#26324b" fontSize="12.5" fontWeight="600">Production to date</text>
                  </g>
                </ImpactMilestone>

                <ImpactMilestone progress={drawProgress} arrival={monthArrivals[2]} x={730} y={monthPoints[2].y} guideTop={monthPoints[2].y - 20} reduceMotion={shouldReduceMotion}>
                  <g transform={`translate(0 ${monthPoints[2].y - 90})`}>
                    <rect x="626" y="0" width="208" height="64" rx="14" fill="#fff" stroke="#e5e0e9" filter="url(#dental-impact-card-shadow)" />
                    <rect x="649" y="15" width="32" height="32" rx="9" fill="#fff5ed" />
                    <Clock3 x={655} y={21} width={20} height={20} color="#e85b0a" strokeWidth={2.2} aria-hidden="true" />
                    <text x="691" y="27" fill="#e85b0a" fontSize="22" fontWeight="800">{estimatedThirdMonthHoursSaved} hrs</text>
                    <text x="691" y="47" fill="#26324b" fontSize="12.5" fontWeight="600">Saved in month 3</text>
                  </g>
                </ImpactMilestone>

                <text x="65" y="323" fill="#25304a" fontSize="11" fontWeight="800">START</text>
                <text x="260" y="323" textAnchor="middle" fill="#25304a" fontSize="11" fontWeight="800">MONTH 1</text>
                <text x="490" y="323" textAnchor="middle" fill="#25304a" fontSize="11" fontWeight="800">MONTH 2</text>
                <text x="730" y="323" textAnchor="middle" fill="#25304a" fontSize="11" fontWeight="800">MONTH 3</text>
              </svg>
            </div>
          </div>

          <aside className="px-1 lg:self-center" aria-labelledby="dental-impact-story-title">
            <h3 id="dental-impact-story-title" className="max-w-[280px] text-[23px] font-extrabold leading-[1.2] tracking-[-.035em] text-[#172033]">
              A warm welcome, day or night.
            </h3>
            <div className="mt-5 space-y-6 text-[14px] leading-[1.7] text-[#626679]">
              <p>
                Invoca’s 2026 dental benchmark found that <strong className="whitespace-nowrap font-bold">only 60%</strong> of callers reached a person.
              </p>
              <p>
                ClawEase AI Receptionist answers routine questions and helps book available appointments <strong className="whitespace-nowrap font-bold">24/7</strong>, even when your front desk is busy or closed.
              </p>
              <p>
                Requests needing a person reach your team with context, so staff can focus on patients.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
